import {useContext, useMemo} from 'react';

import {type LLMMaxTokensKey} from '../pages/PlaygroundPage/llmMaxTokens';
import type {Query} from '../pages/wfReactInterface/traceServerClientInterface/query';
import logoAlibaba from './logos/icon-alibaba.png';
import logoBAAI from './logos/icon-baai.png';
import logoDeepSeek from './logos/icon-deepseek.png';
import logoGoogle from './logos/icon-google.png';
import logoIBM from './logos/icon-ibm.png';
import logoJetBrains from './logos/icon-jetbrains.png';
import logoMeta from './logos/icon-meta.png';
import logoMicrosoft from './logos/icon-microsoft.png';
import logoMiniMaxAI from './logos/icon-minimaxai.png';
import logoMistral from './logos/icon-mistral.png';
import logoMoonshotAI from './logos/icon-moonshot-ai.png';
import logoNVIDIA from './logos/icon-nvidia.png';
import logoOpenAI from './logos/icon-openai.png';
import logoOpenAIDark from './logos/icon-openai-dark.png';
import logoOpenPipe from './logos/icon-openpipe.png';
import logoReflectionAI from './logos/icon-reflection.png';
import logoZAI from './logos/icon-zai.png';
import {
  InferenceContext,
  type InferenceContextType,
  type LifecycleStage,
  type Model,
  type Server,
  type Source,
} from './types';

// TODO: Duplicating this from core - move into context?
export const INFERENCE_PATH = '/inference';

// Prefix stamped on W&B Inference (CoreWeave-hosted) model ids wherever they are
// sent to the completion endpoint and recorded on traces.
export const COREWEAVE_MODEL_PREFIX = 'coreweave/';

// Getting-started docs for W&B Serverless Inference; linked from the inference
// cost stat on the agents dashboard and the project-overview weave tab.
export const SERVERLESS_INFERENCE_DOCS_URL =
  'https://docs.wandb.ai/guides/inference/';

// Span query matching W&B Serverless Inference traffic by its coreweave/-prefixed
// requested model. Shared by the agents-dashboard tile and the project-overview
// weave section so both scope "inference cost" the same way.
export const INFERENCE_MODEL_QUERY: Query = {
  $expr: {
    $contains: {
      input: {$getField: 'request_model'},
      substr: {$literal: COREWEAVE_MODEL_PREFIX},
    },
  },
};

const INFERENCE_API_BASE_URL_QA = 'https://api.qa.inference.wandb.ai/v1';
const INFERENCE_API_BASE_URL_PROD = 'https://api.inference.wandb.ai/v1';

const INFERENCE_SERVER_BY_HOSTNAME: Readonly<Record<string, Server>> = {
  'forge.coreweave.com': 'cw-prod',
  'wandb.ai': 'cw-prod',
  'beta.wandb.ai': 'cw-prod',
  'qa.forge.coreweave.com': 'cw-qa',
  'qa.wandb.ai': 'cw-qa',
};

export const getInferenceServerForHostname = (
  hostname: string
): Server | undefined => INFERENCE_SERVER_BY_HOSTNAME[hostname];

export const isInferenceDevelopmentHostname = (hostname: string): boolean =>
  hostname.endsWith('.wandb.test');

/**
 * OpenAI-compatible W&B Inference API base URL for the current browser host.
 * QA hosts map to the QA inference stack; other hosts (including local dev) use production.
 */
export function getInferenceBaseUrl(): string {
  if (
    typeof window !== 'undefined' &&
    getInferenceServerForHostname(window.location.hostname) === 'cw-qa'
  ) {
    return INFERENCE_API_BASE_URL_QA;
  }
  return INFERENCE_API_BASE_URL_PROD;
}

export function urlInference(collectionId?: string, modelId?: string): string {
  const parts = [INFERENCE_PATH];
  if (collectionId) {
    parts.push(collectionId);
  }
  if (modelId) {
    parts.push(modelId);
  }
  return parts.join('/');
}

const sourceNameMap: Record<Source, string> = {
  BAAI: 'Beijing Academy of Artificial Intelligence (BAAI)',
  deepseek: 'DeepSeek',
  'deepseek-ai': 'DeepSeek',
  google: 'Google',
  'ibm-granite': 'IBM',
  JetBrains: 'JetBrains',
  'meta-llama': 'Meta',
  microsoft: 'Microsoft',
  MiniMaxAI: 'MiniMax',
  mistral: 'Mistral',
  mistralai: 'Mistral',
  moonshotai: 'Moonshot AI',
  nvidia: 'NVIDIA',
  openai: 'OpenAI',
  OpenPipe: 'OpenPipe',
  Qwen: 'Alibaba',
  reflection: 'Reflection',
  xai: 'xAI',
  'zai-org': 'Z.AI',
};

const sourceLogoMap: Record<Source, string> = {
  BAAI: 'baai',
  deepseek: 'deepseek',
  'deepseek-ai': 'deepseek',
  google: 'google',
  'ibm-granite': 'ibm',
  JetBrains: 'jetbrains',
  'meta-llama': 'meta',
  microsoft: 'microsoft',
  MiniMaxAI: 'minimaxai',
  mistral: 'mistral',
  mistralai: 'mistral',
  moonshotai: 'moonshotai',
  nvidia: 'nvidia',
  openai: 'openai',
  OpenPipe: 'openpipe',
  Qwen: 'alibaba',
  reflection: 'reflection',
  'zai-org': 'zai',
};

const logoIdToImageMap: Record<string, any> = {
  alibaba: logoAlibaba,
  baai: logoBAAI,
  deepseek: logoDeepSeek,
  google: logoGoogle,
  ibm: logoIBM,
  jetbrains: logoJetBrains,
  meta: logoMeta,
  microsoft: logoMicrosoft,
  minimaxai: logoMiniMaxAI,
  mistral: logoMistral,
  moonshotai: logoMoonshotAI,
  nvidia: logoNVIDIA,
  openai: logoOpenAI,
  openpipe: logoOpenPipe,
  reflection: logoReflectionAI,
  zai: logoZAI,
};

// Dark-mode logo asset per `logoIdToImageMap` key; omit providers that reuse the light asset.
const logoIdToImageMapDark: Partial<Record<string, string>> = {
  openai: logoOpenAIDark,
};

// First segment of a slash-delimited id
const firstSegment = (id: string): string => {
  return id.split('/')[0] ?? '';
};

export const getModelSource = (model: Model): Source => {
  if (model.source) {
    return model.source;
  }
  const idForSource = model.idPlayground ?? model.idHuggingFace;
  return idForSource ? firstSegment(idForSource) : 'unknown';
};

export const getSourceLogo = (source: Source) => {
  const logoId = sourceLogoMap[source];
  if (!logoId) {
    return null;
  }
  return logoIdToImageMap[logoId] ?? null;
};

export const getSourceLogoDark = (source: Source): string | null => {
  const logoId = sourceLogoMap[source];
  if (!logoId) {
    return null;
  }
  return logoIdToImageMapDark[logoId] ?? null;
};

export const getModelSourceName = (model: Model) => {
  const source = getModelSource(model);
  return sourceNameMap[source];
};

export const getModelLogo = (model: Model): string | null => {
  const source = getModelSource(model);
  return getSourceLogo(source);
};

export const getModelLogoDark = (model: Model): string | null => {
  const source = getModelSource(model);
  return getSourceLogoDark(source);
};

type StageColor = 'green' | 'gold' | 'sienna' | 'red';

type StageInfo = {
  label: string;
  color: StageColor;
  tooltip: string;
};

const stageInfoMap: Record<LifecycleStage, StageInfo> = {
  'general-availability': {
    label: 'General availability',
    color: 'green',
    tooltip: 'This model is fully supported and recommended for use.',
  },
  experimental: {
    label: 'Experimental',
    color: 'gold',
    tooltip:
      'Experimental models may be removed without notice; no SLA applies.',
  },
  deprecated: {
    label: 'Deprecated',
    color: 'sienna',
    tooltip: 'This model is scheduled to be retired.',
  },
  retired: {
    label: 'Retired',
    color: 'red',
    tooltip: 'This model is no longer available.',
  },
};

export const getModelLifecycleInfo = (
  model: Pick<Model, 'lifecycleStage'>
): StageInfo => {
  return stageInfoMap[model.lifecycleStage];
};

export const getModelLabel = (model: Model) => {
  return model.label ?? model.id.split('/').pop();
};

export const DEFAULT_LOCALE = 'en-US';

export const getLaunchDateString = (model: Model) => {
  if (model.launchDate) {
    const date = new Date(model.launchDate);
    return date.toLocaleDateString(DEFAULT_LOCALE, {
      month: 'short',
      year: 'numeric',
    });
  }
  return '';
};

export const getLaunchDateStringMedium = (model: Model) => {
  if (model.launchDate) {
    const date = new Date(model.launchDate);
    return date.toLocaleDateString(DEFAULT_LOCALE, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return '';
};

export const getOnePriceString = (
  priceCentsPerBillionTokens: number
): string => {
  const centsPerDollar = 100;
  const milPerBil = 1000;
  const dollarsPerMillionTokens =
    priceCentsPerBillionTokens / (centsPerDollar * milPerBil);

  // Prices per million tokens are usually whole cents (e.g. $2.50), so we
  // default to 2 decimal places. But some rates work out to fractional
  // cents (e.g. $0.015), and rounding those to 2 decimals would display
  // "$0.02" or "$0.01" and lose the actual rate. Dividing an integer cent
  // amount by centsPerDollar * milPerBil always terminates within 5 decimal
  // digits, so trimming trailing zeros from a fixed 5-decimal string gives
  // the exact number of decimal places needed, with no floating-point
  // rounding guesswork.
  const fixed = dollarsPerMillionTokens.toFixed(5);
  const trimmed = fixed.replace(/0+$/, '').replace(/\.$/, '');
  const decimalPlaces = trimmed.includes('.')
    ? trimmed.split('.')[1].length
    : 0;

  // Prefix with literal dollar sign
  return `$${dollarsPerMillionTokens.toFixed(Math.max(2, decimalPlaces))}`;
};

export const getPriceString = (
  model: Model,
  includeLabel: boolean = true,
  separator: string = '/'
) => {
  const inputPrice = model.priceCentsPerBillionTokensInput;
  const cachedPrice = model.priceCentsPerBillionTokensCached;
  const outputPrice = model.priceCentsPerBillionTokensOutput;

  const segment = (price: number, label: string) =>
    `${getOnePriceString(price)}${includeLabel ? ` ${label}` : ''}`;

  const parts: string[] = [];
  if (inputPrice != null) {
    parts.push(segment(inputPrice, 'input'));
  }
  if (cachedPrice != null && cachedPrice !== inputPrice) {
    parts.push(segment(cachedPrice, 'cached'));
  }
  if (outputPrice != null) {
    parts.push(segment(outputPrice, 'output'));
  }
  return parts.join(` ${separator} `);
};

export const getParameterCountString = (model: Model) => {
  const total = model.parameterCountTotal ?? 0;
  const active = model.parameterCountActive ?? 0;
  if (active) {
    return `${getShortNumberString(active)} - ${getShortNumberString(total)}`;
  }
  return getShortNumberString(total);
};

const divideAndRound = (
  value: number,
  divisor: number,
  suffix: string,
  fractionDigits?: number
): string => {
  if (divisor === 0) {
    throw new Error('Divisor cannot be zero.');
  }
  const result = value / divisor;
  if (fractionDigits != null) {
    return result.toFixed(fractionDigits) + suffix;
  }
  return result.toString() + suffix;
};

export const getShortNumberString = (
  count: number,
  fractionDigits?: number
): string => {
  if (count >= 1000000000000) {
    return divideAndRound(count, 1000000000000, 'T', fractionDigits);
  } else if (count >= 1000000000) {
    return divideAndRound(count, 1000000000, 'B', fractionDigits);
  } else if (count >= 1000000) {
    return divideAndRound(count, 1000000, 'M', fractionDigits);
  } else if (count >= 1000) {
    return divideAndRound(count, 1000, 'k', fractionDigits);
  }
  return `${count}`;
};

export const getContextWindowString = (model: Model): string => {
  return model.contextWindow != null
    ? getShortNumberString(model.contextWindow, 0)
    : '';
};

export const getModelLicense = (model: Model): string => {
  return 'License: ' + (model.license ?? 'Unknown');
};

// A default object for when we don't have an inference context.
export const getDefaultInferenceContext = (): InferenceContextType => {
  return {
    isLoggedIn: false,
    isServerDeployment: false,
    isCatalogEnabled: false,
    isInferenceEnabled: false,
    availabilityMessage: '',
    playgroundEntity: '',
    playgroundProject: '',
    projectExists: false,
    ensureProjectExists: () => Promise.resolve(),
    billing: null,
  };
};

export const useInferenceContext = (): InferenceContextType => {
  // TODO: Once we have added provider in core we could throw an error if there is no context.
  const context = useContext(InferenceContext);
  return context ?? getDefaultInferenceContext();
};

export const getPlaygroundModelString = (model: Model): LLMMaxTokensKey => {
  return `${COREWEAVE_MODEL_PREFIX}${model.idPlayground}` as LLMMaxTokensKey;
};

/**
 * Determines if a user can access W&B Inference based on their billing status and account type.
 * This utility matches the logic from QuickStartWeave.tsx.
 */
export const useCanAccessInference = ({
  isInferenceEnabled,
  billing,
}: Pick<InferenceContextType, 'isInferenceEnabled' | 'billing'>) => {
  // Check if user has token balance remaining
  const hasTokenBalance = useMemo(() => {
    if (!billing) {
      return false;
    }
    if (
      billing.inferenceFreeLimit === undefined ||
      billing.inferenceFreeLimit === null
    ) {
      return false;
    }
    if (billing.usage === undefined || billing.usage === null) {
      return false;
    }
    return billing.inferenceFreeLimit - billing.usage > 0;
  }, [billing]);

  // Personal accounts have a very high credit limit, but the backend doesn't provide
  // this limit information through GraphQL so we have to just assume there is sufficient token balance.
  // See comments in InferenceContext.tsx
  const isPersonalAccount = billing?.accountPlanType === 'Personal';

  // User can access W&B Inference if they have inference enabled AND (have token balance OR have pay-as-you-go OR using personal entity)
  const canAccessInference = useMemo(() => {
    if (!isInferenceEnabled) {
      return false;
    }
    return isPersonalAccount || hasTokenBalance || billing?.isUsagePaid;
  }, [
    isInferenceEnabled,
    isPersonalAccount,
    hasTokenBalance,
    billing?.isUsagePaid,
  ]);

  return {
    canAccessInference,
    hasTokenBalance,
  };
};
